import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Github, Linkedin, Menu, UserPlus, X } from 'lucide-react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { EARLY_ACCESS_BADGE, MARKETING_CTA, MARKETING_PRODUCT_ITEMS } from './marketingConfig';

const NAV_ITEMS = [
  { to: '/', label: 'Pulse' },
  { to: '/features', label: 'Features' },
  { to: '/how-it-works', label: 'How it works' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/about', label: 'About' },
  { to: '/docs', label: 'Docs' },
];

export function MarketingLayout() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const productsButtonRef = useRef<HTMLButtonElement | null>(null);
  const firstProductLinkRef = useRef<HTMLAnchorElement | null>(null);
  const firstMobileLinkRef = useRef<HTMLAnchorElement | null>(null);
  const productMenuId = 'marketing-products-menu';

  useEffect(() => {
    setMobileOpen(false);
    setProductOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    firstMobileLinkRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen]);

  return (
    <div className="min-h-screen bg-surface-0 text-gray-100">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-brand focus:px-3 focus:py-2 focus:text-white"
      >
        Skip to main content
      </a>

      <header className="sticky top-0 z-40 border-b border-surface-4/80 bg-surface-0/85 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight">
            <img src="/logo.png" alt="Navyrx Sports" className="h-7 w-7 object-contain flex-shrink-0" />
            <span>
              Navyrx<span className="text-brand"> Sports</span>
            </span>
          </Link>

          <nav aria-label="Primary" className="hidden items-center gap-1 md:flex">
            <p className="mr-2 inline-flex items-center rounded-full border border-sky-400/30 bg-sky-400/10 px-2.5 py-1 text-[10px] uppercase tracking-wide text-sky-200">
              {EARLY_ACCESS_BADGE.label} · {EARLY_ACCESS_BADGE.detail}
            </p>
            <div
              className="relative"
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setProductOpen(false);
                }
              }}
            >
              <button
                type="button"
                ref={productsButtonRef}
                onMouseEnter={() => setProductOpen(true)}
                onFocus={() => setProductOpen(true)}
                onClick={() => setProductOpen((v) => !v)}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown' && !productOpen) {
                    event.preventDefault();
                    setProductOpen(true);
                    setTimeout(() => firstProductLinkRef.current?.focus(), 0);
                  }
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    setProductOpen(false);
                    productsButtonRef.current?.focus();
                  }
                }}
                className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm text-gray-300 transition-colors hover:bg-surface-3"
                aria-expanded={productOpen}
                aria-haspopup="menu"
                aria-controls={productMenuId}
              >
                Products <ChevronDown className="h-3.5 w-3.5" />
              </button>
              {productOpen && (
                <div
                  id={productMenuId}
                  role="menu"
                  className="absolute right-0 top-11 w-80 rounded-xl border border-surface-4 bg-surface-1 p-2 shadow-2xl"
                  onMouseLeave={() => setProductOpen(false)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      setProductOpen(false);
                      productsButtonRef.current?.focus();
                    }
                  }}
                >
                  {MARKETING_PRODUCT_ITEMS.map((item, index) => (
                    <Link
                      key={item.label}
                      role="menuitem"
                      ref={index === 0 ? firstProductLinkRef : undefined}
                      to={item.to}
                      className="block rounded-lg px-3 py-2 hover:bg-surface-2"
                      onClick={() => setProductOpen(false)}
                    >
                      <p className="text-sm font-medium text-gray-100">{item.label}</p>
                      <p className="text-xs text-gray-400">{item.detail}</p>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            {NAV_ITEMS.map((item, index) => (
              <NavLink
                key={`desktop-${item.label}-${index}`}
                to={item.to}
                className={({ isActive }) =>
                  [
                    'rounded-md px-3 py-2 text-sm transition-colors',
                    isActive ? 'bg-brand/10 text-brand' : 'text-gray-300 hover:bg-surface-3',
                  ].join(' ')
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              ref={menuButtonRef}
              aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileOpen}
              aria-controls="mobile-marketing-nav"
              className="rounded-md border border-surface-4 p-2 text-gray-300 hover:bg-surface-3 md:hidden"
              onClick={() => setMobileOpen((open) => !open)}
            >
              {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
            <Link to="/login" className="btn-ghost !px-3 !py-1.5">
              Login
            </Link>
            <Link to={MARKETING_CTA.requestAccess.to} title="Request Access" aria-label="Request Access">
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.93 }}
                className="group relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-cyan-400 via-blue-500 to-violet-600 text-white shadow-[0_4px_18px_rgba(0,123,255,0.35)] border border-white/10 transition-shadow duration-300"
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.2),transparent_70%)]" />
                <UserPlus className="relative z-10 h-4 w-4" />
              </motion.div>
            </Link>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <nav id="mobile-marketing-nav" aria-label="Mobile primary" className="border-b border-surface-4 bg-surface-1 md:hidden">
          <div className="mx-auto max-w-7xl space-y-1 px-4 py-3">
            <p className="px-3 pb-1 text-[11px] uppercase tracking-wide text-gray-500">Products</p>
            {MARKETING_PRODUCT_ITEMS.map((item, index) => (
              <NavLink
                key={item.label}
                ref={index === 0 ? firstMobileLinkRef : undefined}
                to={item.to}
                className="block rounded-md px-3 py-2 text-sm text-gray-300 hover:bg-surface-3"
              >
                {item.label}
              </NavLink>
            ))}
            <div className="my-2 border-t border-surface-4" />
            {NAV_ITEMS.map((item, index) => (
              <NavLink
                key={`mobile-${item.label}-${index}`}
                to={item.to}
                className={({ isActive }) =>
                  ['block rounded-md px-3 py-2 text-sm', isActive ? 'bg-brand/10 text-brand' : 'text-gray-300 hover:bg-surface-3'].join(' ')
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="grid grid-cols-2 gap-2 pt-2">
              <Link to="/login" className="btn-ghost border border-surface-4 text-center">
                Login
              </Link>
              <Link to={MARKETING_CTA.requestAccess.to} className="btn-primary text-center">
                {MARKETING_CTA.requestAccess.label}
              </Link>
            </div>
          </div>
        </nav>
      )}

      <main id="main-content">
        <Outlet />
      </main>

      <footer className="mt-20 border-t border-surface-4">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-10 sm:px-6 lg:grid-cols-4 lg:px-8">
          <div>
            <p className="font-semibold">Navyrx Sports</p>
            <p className="mt-2 text-sm text-gray-400">Institutional-grade sports intelligence with transparency-first process controls.</p>
            <p className="mt-2 text-xs text-gray-500">
              Access expands gradually through controlled onboarding waves to preserve platform calibration quality.
            </p>
            <p className="mt-4 text-xs text-gray-500">
              Responsible use: Navyrx Sports does not guarantee outcomes and should not be used for impulsive wagering behavior.
            </p>
          </div>

          <div>
            <h2 className="text-sm font-semibold">Ecosystem</h2>
            <ul className="mt-3 space-y-2 text-sm text-gray-400">
              <li><Link to="/" className="hover:text-gray-200">Pulse Command Center</Link></li>
              {MARKETING_PRODUCT_ITEMS.map((item) => (
                <li key={`footer-${item.label}`}>
                  <Link to={item.to} className="hover:text-gray-200">{item.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="text-sm font-semibold">Platform</h2>
            <ul className="mt-3 space-y-2 text-sm text-gray-400">
              <li><Link to="/how-it-works" className="hover:text-gray-200">See Recommendation Lifecycle</Link></li>
              <li><Link to="/features" className="hover:text-gray-200">See Validation Engine</Link></li>
              <li><Link to="/pricing" className="hover:text-gray-200">Compare Plans</Link></li>
              <li><Link to="/about" className="hover:text-gray-200">Enterprise Contact</Link></li>
            </ul>
          </div>

          <div>
            <h2 className="text-sm font-semibold">Company</h2>
            <ul className="mt-3 space-y-2 text-sm text-gray-400">
              <li><Link to="/about" className="hover:text-gray-200">About</Link></li>
              <li><a href="mailto:support@navyrx-sports.com" className="hover:text-gray-200">Contact</a></li>
              <li><Link to="/terms" className="hover:text-gray-200">Terms</Link></li>
              <li><Link to="/privacy" className="hover:text-gray-200">Privacy</Link></li>
              <li><Link to="/responsible-use" className="hover:text-gray-200">Responsible use</Link></li>
              <li className="flex items-center gap-3 pt-1">
                <button type="button" disabled aria-label="GitHub (coming soon)" aria-disabled="true" className="cursor-not-allowed text-gray-500">
                  <Github className="h-4 w-4" />
                </button>
                <button type="button" disabled aria-label="LinkedIn (coming soon)" aria-disabled="true" className="cursor-not-allowed text-gray-500">
                  <Linkedin className="h-4 w-4" />
                </button>
              </li>
              <li className="pt-2 text-xs text-gray-600">© {new Date().getFullYear()} Navyrx Sports</li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}
